import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Trophy, Medal, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [showTop10, setShowTop10] = useState(false);
  
  useEffect(() => {
    fetchLeaderboard();
  }, []);
  
  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(response.data);
    } catch (error) {
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading || !leaderboardData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading leaderboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  const getBadgeIcon = (badge) => {
    if (badge === 'Gold') return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (badge === 'Silver') return <Medal className="w-6 h-6 text-gray-400" />;
    if (badge === 'Bronze') return <Award className="w-6 h-6 text-orange-600" />;
    return null;
  };
  
  const displayList = showTop10 ? leaderboardData.leaderboard : leaderboardData.leaderboard.slice(0, 5);
  
  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">LEADERBOARD</h1>
        
        <div className="max-w-4xl">
          <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[18px] font-bold" style={{ color: '#0056B3' }}>
              {leaderboardData.config.leaderboard_scope === 'department_wise' ? 'Department' : 'Institution-wide'} Rankings
            </h2>
            {leaderboardData.leaderboard.length > 5 && (
              <Button variant="outline" size="sm" onClick={() => setShowTop10(!showTop10)}>
                {showTop10 ? 'Show Top 5' : 'Show Top 10'}
              </Button>
            )}
          </div>
          {leaderboardData.leaderboard.length === 0 ? (
            /* Empty state — no staff activity yet */
            <div className="flex flex-col items-center justify-center text-center py-14 px-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner">
                <Trophy className="w-8 h-8 text-[#0056B3]" />
              </div>
              <div className="flex items-end justify-center gap-1.5 mt-4" aria-hidden="true">
                <div className="w-7 h-7 rounded-t-md bg-gray-200" />
                <div className="w-7 h-11 rounded-t-md bg-blue-200" />
                <div className="w-7 h-5 rounded-t-md bg-gray-200" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mt-5">Your leaderboard will appear here</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm leading-relaxed">
                The leaderboard will appear once your {leaderboardData.institution_type || 'institution'} starts completing training.
              </p>
            </div>
          ) : (
          <div className="space-y-4">
            {displayList.map((entry, idx) => (
              <div key={entry.user_id} className={`flex items-center justify-between p-4 border rounded-lg ${
                entry.user_id === leaderboardData.own_rank?.user_id ? 'bg-blue-50 border-[#0056B3] border-2' : ''
              }`}>
                <div className="flex items-center gap-3 flex-1" style={{ minWidth: 0 }}>
                  <div className="w-10 text-center flex-shrink-0">
                    {entry.badge ? getBadgeIcon(entry.badge) : <span className="text-base font-bold text-gray-400">#{entry.rank}</span>}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p className="font-semibold text-base mb-0.5" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.name}</p>
                    <p className="text-xs text-gray-600" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.department}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 text-right ml-4">
                  <p className="text-lg font-bold" style={{ color: '#0056B3', whiteSpace: 'nowrap' }}>{entry.score}%</p>
                  {entry.badge && <Badge className="mt-1 bg-yellow-100 text-yellow-800">{entry.badge}</Badge>}
                </div>
              </div>
            ))}
          </div>
          )}
          {leaderboardData.own_rank && leaderboardData.own_rank.rank > 10 && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-600 mb-3">Your Position:</p>
              <div className="flex items-center justify-between p-4 bg-blue-50 border-2 border-[#0056B3] rounded-lg">
                <div className="flex items-center gap-3 flex-1" style={{ minWidth: 0 }}>
                  <span className="text-base font-bold text-[#0056B3] flex-shrink-0">#{leaderboardData.own_rank.rank}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p className="font-semibold text-base mb-0.5" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{leaderboardData.own_rank.name}</p>
                    <p className="text-xs text-gray-600" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{leaderboardData.own_rank.department}</p>
                  </div>
                </div>
                <p className="text-lg font-bold flex-shrink-0 ml-4" style={{ color: '#0056B3', whiteSpace: 'nowrap' }}>{leaderboardData.own_rank.score}%</p>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeaderboardPage;